import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

/* ============================================================================
   THEMES-01 · ThemeContext / ThemeProvider
   Contrato:
     themePreference : "clinical" | "blue" | "dark" | "system"   (lo que eligió el usuario)
     resolvedTheme   : "clinical" | "blue" | "dark"              (lo que se pinta)
     setTheme(pref)  : valida, actualiza estado y persiste en localStorage

   El Context SÓLO controla estado. La apariencia la controlan las CSS variables
   de src/styles/theme-tokens.css. Aquí no se guardan objetos de color.

   Sincroniza además los atributos del tema Skote en <body> (data-layout-mode /
   data-sidebar / data-topbar) — son los que realmente consume public/css/app.css.
   ========================================================================== */

export const STORAGE_KEY = "smartmedic.theme";
export const VALID_PREFERENCES = ["clinical", "blue", "dark", "system"];
export const DEFAULT_PREFERENCE = "clinical";

/** Cualquier valor fuera del contrato -> "clinical". */
export function normalizePreference(value) {
  return VALID_PREFERENCES.includes(value) ? value : DEFAULT_PREFERENCE;
}

/** "system"/"Automático" resuelve a "dark" si el SO prefiere oscuro, si no a
 *  "clinical". NO existe una paleta propia de "system". */
export function resolveTheme(preference, systemPrefersDark) {
  const pref = normalizePreference(preference);
  if (pref === "system") return systemPrefersDark ? "dark" : "clinical";
  return pref;
}

/** Mapping tema -> atributos Skote (<body>). Deben coincidir con el script
 *  anti-FOUC de public/index.html. */
export const SKOTE_ATTRS = {
  clinical: { "data-layout-mode": "light", "data-sidebar": "light", "data-topbar": "light" },
  blue: { "data-layout-mode": "light", "data-sidebar": "dark", "data-topbar": "light" },
  dark: { "data-layout-mode": "dark", "data-sidebar": "dark", "data-topbar": "dark" },
};

function readStoredPreference() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return DEFAULT_PREFERENCE;
    return normalizePreference(window.localStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return DEFAULT_PREFERENCE;
  }
}

function getSystemPrefersDark() {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  } catch (e) {
    return false;
  }
}

/** Estampa el tema resuelto en el DOM: <html data-theme>, atributos Skote en
 *  <body> y <meta name="theme-color">. Idempotente y con guard SSR. */
export function applyThemeToDom(resolvedTheme) {
  if (typeof document === "undefined") return;
  try {
    const t = ["clinical", "blue", "dark"].includes(resolvedTheme) ? resolvedTheme : "clinical";
    document.documentElement.dataset.theme = t;

    if (document.body) {
      const attrs = SKOTE_ATTRS[t] || SKOTE_ATTRS.clinical;
      Object.keys(attrs).forEach((k) => document.body.setAttribute(k, attrs[k]));
    }

    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", t === "dark" ? "#151b23" : "#ffffff");
  } catch (e) {
    /* noop */
  }
}

const ThemeContext = createContext({
  themePreference: DEFAULT_PREFERENCE,
  resolvedTheme: DEFAULT_PREFERENCE,
  setTheme: () => {},
});

export function ThemeProvider({ children }) {
  const [themePreference, setThemePreference] = useState(readStoredPreference);
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);

  const resolvedTheme = resolveTheme(themePreference, systemPrefersDark);

  // Seguir EN VIVO el prefers-color-scheme del SO (sin reload). Listener limpio.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event) => setSystemPrefersDark(!!event.matches);

    if (typeof mql.addEventListener === "function") mql.addEventListener("change", onChange);
    else if (typeof mql.addListener === "function") mql.addListener(onChange);

    return () => {
      if (typeof mql.removeEventListener === "function") mql.removeEventListener("change", onChange);
      else if (typeof mql.removeListener === "function") mql.removeListener(onChange);
    };
  }, []);

  // Reflejar el tema resuelto en el DOM.
  useEffect(() => {
    applyThemeToDom(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback((next) => {
    const normalized = normalizePreference(next);
    setThemePreference(normalized);
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(STORAGE_KEY, normalized);
      }
    } catch (e) {
      /* noop — modo privado / storage deshabilitado */
    }
  }, []);

  const value = useMemo(
    () => ({ themePreference, resolvedTheme, setTheme }),
    [themePreference, resolvedTheme, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export default ThemeContext;
